import { useHead } from '@unhead/vue'

const BASE_URL = 'https://www.getdigitly.com'
const SITE_NAME = 'Digitly'
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`

export function useSeo(options = {}) {
  const {
    title,
    description,
    keywords = '',
    path = '',
    image = DEFAULT_IMAGE,
    type = 'website',
    noIndex = false
  } = options

  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Professional Web Development & Business Automation`
  const canonicalUrl = `${BASE_URL}${path}`

  useHead({
    title: fullTitle,
    meta: [
      // Primary Meta Tags
      { name: 'title', content: fullTitle },
      { name: 'description', content: description },
      ...(keywords ? [{ name: 'keywords', content: keywords }] : []),
      ...(noIndex ? [{ name: 'robots', content: 'noindex, nofollow' }] : [{ name: 'robots', content: 'index, follow' }]),
      
      // Open Graph / Facebook
      { property: 'og:type', content: type },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:locale', content: 'en_GB' },
      
      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:url', content: canonicalUrl },
      { name: 'twitter:title', content: fullTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image }
    ],
    link: [
      { rel: 'canonical', href: canonicalUrl }
    ]
  })
}

// Structured data helpers
export function useServiceSchema(service) {
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          'name': service.name,
          'description': service.description,
          'provider': {
            '@type': 'Organization',
            'name': 'Digitly',
            'url': BASE_URL
          },
          'areaServed': {
            '@type': 'Country',
            'name': 'United Kingdom'
          }
        })
      }
    ]
  })
}

export function useFaqSchema(faqs) {
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': faqs.map(faq => ({
            '@type': 'Question',
            'name': faq.question,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': faq.answer
            }
          }))
        })
      }
    ]
  })
}

export function useBreadcrumbSchema(items) {
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': items.map((item, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'name': item.name,
            'item': `${BASE_URL}${item.path}`
          }))
        })
      }
    ]
  })
}
